import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FiArchive, FiPlus, FiMail } from "react-icons/fi"

import "./index.scss"
import useT from "utils/hooks/useT"
import { ConfirmDialog, Popup } from "components"
import { members as membersState, permissions, limsCapabilities, projects } from "state_management"
import { QA, QA_MANAGER } from "state_management/lims/limsCapabilities"
import ESignatureModal from "components/Lims/ESignatureModal"
import QaMemberConfig from "./QaMemberConfig"

const LAB_CAPABILITIES = [
  { id: "analyst", labelKey: "limsCapAnalyst" },
  { id: "reviewer", labelKey: "limsCapReviewer" },
  { id: "supervisor", labelKey: "limsCapSupervisor" },
]

const ROLE_LABEL_KEYS = { MEMBER: "member", PLANNER: "planner", VIEW_ONLY: "viewOnly" }

// LIMS › QA team (SPEC-058 A, Plannertech-user#4115). The quality column's own staff page: the
// QA Manager (a MEMBER-role member holding `qa-manager`) assigns and revokes the `qa` role with
// an e-signature, adds QA members (always the Member planning role), archives QA-role holders
// and sends them a password mail. It REPLACES the QA Manager's old entry into Setup › Team,
// where every planning field was read-only for them without saying why — here nothing is
// ghosted because the page only holds what they can do. The Planner may open the page read-only
// to see who holds QA; nobody else sees the menu item (LimsScreen gates the section).
//
// Every decision helper below is a mirror of a server rule (permissions.js) and the server stays
// the law: a refused call is toasted from the message layer.
export default function QaTeam() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const canManage = useSelector(permissions.selectCanManageQaTeam)
  const authRole = useSelector((state) => state.auth.role)
  const authMemberId = useSelector((state) => state.auth.memberId)
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const members = Object.values(useSelector((state) => state.members))
    .filter((member) => !member.archived)
    .sort((a, b) => (a.initials || "").localeCompare(b.initials || ""))

  const [adding, setAdding] = useState(false)
  // A pending QA-role change awaiting its e-signature: { member, nextCapabilities } or null.
  const [qaChange, setQaChange] = useState(null)
  const [activeArchive, setActiveArchive] = useState(null)

  useEffect(() => {
    dispatch(membersState.fetch())
  }, [dispatch])

  const holds = (member, capability) => (member.qcCapabilities || []).includes(capability)

  const startQaChange = (member) => {
    const current = member.qcCapabilities || []
    const next = holds(member, QA)
      ? current.filter((capability) => capability !== QA)
      : limsCapabilities.QC_CAPABILITIES.filter((capability) => current.includes(capability) || capability === QA)
    setQaChange({ member, nextCapabilities: next })
  }

  // The signature payload rides on the e-signed POST /lims/qc-capabilities (SPEC-021 B3). The
  // promise is returned so the dialog stays open with an inline error on a 403 and closes on success.
  const signQaChange = (signature) =>
    dispatch(limsCapabilities.setForMember(qaChange.member.id, qaChange.nextCapabilities, signature)).then(() =>
      setQaChange(null)
    )

  const handleArchive = () => {
    dispatch(membersState.archive(activeArchive.id))
      .then(() => dispatch(projects.fetch()))
      // The toast (incl. the translated 409 MEMBER_HOLDS_QA_ROLE fallback) comes from the message
      // layer — just close the dialog either way.
      .catch(() => {})
      .finally(() => setActiveArchive(null))
  }

  return (
    <div className="QaTeam">
      {qaChange && (
        <ESignatureModal title={t("limsEsigTitle")} onSign={signQaChange} onCancel={() => setQaChange(null)} />
      )}
      {activeArchive && (
        <ConfirmDialog onCancel={() => setActiveArchive(null)} onConfirm={handleArchive}>
          <h4>{t("confirmArchivalOfMember", { initials: activeArchive.initials })}</h4>
        </ConfirmDialog>
      )}
      {adding && (
        <Popup nonScrollable onCancel={() => setAdding(false)}>
          <QaMemberConfig onClose={() => setAdding(false)} />
        </Popup>
      )}

      <div className="QaTeam-header">
        <div>
          <h2>{t("limsQaTeam")}</h2>
          <p className="QaTeam-intro">{canManage ? t("limsQaTeamIntro") : t("limsQaTeamPlannerReadOnly")}</p>
        </div>
        {canManage && (
          <Button variant="primary" onClick={() => setAdding(true)}>
            <FiPlus /> {t("limsQaTeamAddMember")}
          </Button>
        )}
      </div>

      <Table hover responsive className="QaTeam-table">
        <thead>
          <tr>
            <th>{t("initials")}</th>
            <th>{t("emailLabel")}</th>
            <th>{t("role")}</th>
            <th>{t("limsQaTeamLabQualifications")}</th>
            <th>{t("limsQaTeamQaRole")}</th>
            {canManage && <th className="QaTeam-actions-head" />}
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const isQaManager = holds(member, QA_MANAGER)
            const isQa = holds(member, QA)
            const qaRole = permissions.qaRoleDecision({
              actorIsQaManager: canManage,
              actorMemberId: authMemberId,
              target: member,
            })
            const mail = permissions.passwordMailDecision({
              actorRole: authRole,
              actorIsQaManager: canManage,
              actorMemberId: authMemberId,
              target: member,
            })
            const archive = permissions.archiveDecision({
              actorRole: authRole,
              actorIsQaManager: canManage,
              actorMemberId: authMemberId,
              showLims,
              target: member,
            })
            return (
              <tr key={member.id}>
                <td className="QaTeam-initials">{member.initials}</td>
                <td>{member.email}</td>
                <td>{t(ROLE_LABEL_KEYS[member.role] || "member")}</td>
                <td>
                  {LAB_CAPABILITIES.filter(({ id }) => holds(member, id)).map(({ id, labelKey }) => (
                    <span key={id} className="QaTeam-badge QaTeam-badge-lab">
                      {t(labelKey)}
                    </span>
                  ))}
                </td>
                <td>
                  {isQaManager && (
                    <span className="QaTeam-badge QaTeam-badge-qa" title={t("limsCapQaManager")}>
                      QM
                    </span>
                  )}
                  {isQa && (
                    <span className="QaTeam-badge QaTeam-badge-qa" title={t("limsCapQa")}>
                      QA
                    </span>
                  )}
                </td>
                {canManage && (
                  <td className="QaTeam-actions">
                    {qaRole.allowed && (
                      <Button size="sm" variant={isQa ? "outline-secondary" : "outline-primary"} onClick={() => startQaChange(member)}>
                        {t(isQa ? "limsQaTeamRemoveQa" : "limsQaTeamAssignQa")}
                      </Button>
                    )}
                    {!qaRole.allowed && qaRole.tooltipKey && (
                      <span className="blocked-action-wrap" title={t(qaRole.tooltipKey)}>
                        <Button size="sm" variant="outline-secondary" className="blocked-action-btn" disabled>
                          {t(isQa ? "limsQaTeamRemoveQa" : "limsQaTeamAssignQa")}
                        </Button>
                      </span>
                    )}
                    {mail.allowed && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        title={t("sendResetMail")}
                        onClick={() => dispatch(membersState.sendPasswordMail(member.id)).catch(() => {})}
                      >
                        <FiMail /> {t("sendResetMail")}
                      </Button>
                    )}
                    {archive.allowed && (
                      <FiArchive
                        className="QaTeam-archive"
                        title={t("archiveMember")}
                        onClick={() => setActiveArchive(member)}
                      />
                    )}
                    {!archive.allowed && archive.tooltipKey && (
                      <FiArchive className="QaTeam-archive blocked-action-icon" title={t(archive.tooltipKey)} />
                    )}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
    </div>
  )
}
